import { Key01Icon, Mail01Icon, UserIcon } from "@hugeicons/core-free-icons";
import { createFileRoute } from "@tanstack/react-router";
import { PageContent } from "@/components/layout/page-content";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StepUpTotpDialog } from "@/components/ui/step-up-totp-dialog";
import { ConfirmDialog } from "@/features/account/components/confirm-dialog";
import { EnrollTotpDialog } from "@/features/account/components/enroll-totp-dialog";
import { InfoRow } from "@/features/account/components/info-row";
import { SessionsSection } from "@/features/account/components/sessions-section";
import { TotpSection } from "@/features/account/components/totp-section";
import { useSecurityPage } from "@/features/account/hooks/use-security-page";
import { useUser } from "@/hooks/use-user";
import { formatFullName } from "@/lib/utils";

export const Route = createFileRoute("/(dashboard)/account/security")({
	component: SecurityPage,
});

function SecurityPage() {
	const { user } = useUser();
	const {
		isResetting,
		currentSessionId,
		sessions,
		sessionsLoading,
		selectedSessionId,
		setSelectedSessionId,
		isRevoking,
		factors,
		factorsLoading,
		showQrModal,
		qrData,
		isEnrolling,
		isVerifying,
		selectedFactorId,
		setSelectedFactorId,
		isDeletingFactor,
		handlePasswordReset,
		handleRevoke,
		handleEnrollTotp,
		handleVerifyTotp,
		handleQrModalClose,
		handleDeleteFactor,
		stepUpDialogProps,
	} = useSecurityPage();

	const displayName = formatFullName(user.firstName, user.lastName);

	return (
		<PageContent title="Security">
			<div className="space-y-6">
				<div className="flex flex-col gap-2">
					<h2 className="text-2xl font-bold">Security</h2>
					<p className="text-muted-foreground">
						Manage your account details and password.
					</p>
				</div>

				<Card className="p-4 sm:p-6">
					<h3 className="text-lg font-semibold mb-4">Account Details</h3>
					<div className="space-y-4">
						<InfoRow icon={UserIcon} label="Name" value={displayName} />
						<InfoRow icon={Mail01Icon} label="Email" value={user.email ?? ""} />
						<InfoRow
							icon={Key01Icon}
							label="Password"
							value="••••••••"
							action={
								<Button
									variant="outline"
									size="sm"
									disabled={isResetting}
									onClick={handlePasswordReset}
								>
									{isResetting ? "Sending..." : "Change"}
								</Button>
							}
						/>
					</div>
				</Card>

				<SessionsSection
					sessions={sessions}
					sessionsLoading={sessionsLoading}
					currentSessionId={currentSessionId}
					onRevoke={setSelectedSessionId}
				/>

				<TotpSection
					factors={factors}
					factorsLoading={factorsLoading}
					isEnrolling={isEnrolling}
					onEnroll={handleEnrollTotp}
					onDeleteFactor={setSelectedFactorId}
				/>
			</div>

			<ConfirmDialog
				open={!!selectedSessionId && !isRevoking}
				onOpenChange={(open) => {
					if (!open) setSelectedSessionId(null);
				}}
				onConfirm={handleRevoke}
				title="Revoke Session?"
				description="This will sign out that device immediately. The user will need to log in again."
				confirmLabel="Revoke"
			/>

			<ConfirmDialog
				open={!!selectedFactorId && !isDeletingFactor}
				onOpenChange={(open) => {
					if (!open) setSelectedFactorId(null);
				}}
				onConfirm={handleDeleteFactor}
				title="Remove Authenticator?"
				description="This will remove your TOTP authenticator. You will no longer be required to use 2FA on login."
				confirmLabel="Remove"
				destructive
			/>

			<EnrollTotpDialog
				open={showQrModal}
				qrData={qrData}
				isVerifying={isVerifying}
				onOpenChange={handleQrModalClose}
				onVerify={handleVerifyTotp}
			/>

			<StepUpTotpDialog {...stepUpDialogProps} />
		</PageContent>
	);
}
